import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { FileText, Plus, Search, Download, Mail, Copy, Trash2, Eye, FilePenLine } from 'lucide-react';
import { toast } from 'sonner';
import { getCsrfToken } from '../../lib/api';

const API = ''; // use relative URLs for production-safe API calls

const STATUS_CONFIG = {
  draft:    { label: 'Entwurf',       className: 'bg-gray-100 text-gray-700 border-gray-200' },
  sent:     { label: 'Gesendet',      className: 'bg-blue-100 text-blue-700 border-blue-200' },
  signed:   { label: 'Unterschrieben', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  completed:{ label: 'Abgeschlossen', className: 'bg-purple-100 text-purple-700 border-purple-200' },
  cancelled:{ label: 'Storniert',     className: 'bg-rose-100 text-rose-700 border-rose-200' },
};

export default function AdminContracts() {
  const [contracts, setContracts] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (search) params.set('search', search);
      const res = await fetch(`${API}/api/admin/contracts?${params}`, { credentials: 'include' });
      if (res.ok) setContracts(await res.json());
    } catch (e) {
      toast.error('Fehler beim Laden: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search, filterStatus]); // eslint-disable-line

  const handleDownload = async (id, number) => {
    const res = await fetch(`${API}/api/admin/contracts/${id}/pdf`, { credentials: 'include' });
    if (!res.ok) return toast.error('PDF konnte nicht geladen werden');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Vertrag_${number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleEmail = async (id, email) => {
    if (!email) return toast.error('Keine E-Mail-Adresse beim Vertrag hinterlegt');
    if (!window.confirm(`Vertrag per E-Mail an ${email} senden?`)) return;
    const res = await fetch(`${API}/api/admin/contracts/${id}/send-email`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() || '' },
      body: JSON.stringify({}),
    });
    if (res.ok) { toast.success('Vertrag versendet'); load(); }
    else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Senden fehlgeschlagen'); }
  };

  const handleDuplicate = async (id) => {
    const res = await fetch(`${API}/api/admin/contracts/${id}/duplicate`, {
      method: 'POST', credentials: 'include',
      headers: { 'X-CSRF-Token': getCsrfToken() || '' },
    });
    if (res.ok) { toast.success('Vertrag dupliziert'); load(); }
    else toast.error('Duplizieren fehlgeschlagen');
  };

  const handleDelete = async (id, number) => {
    if (!window.confirm(`Vertrag ${number} wirklich löschen?`)) return;
    const res = await fetch(`${API}/api/admin/contracts/${id}`, {
      method: 'DELETE', credentials: 'include',
      headers: { 'X-CSRF-Token': getCsrfToken() || '' },
    });
    if (res.ok) { toast.success('Vertrag gelöscht'); load(); }
  };

  return (
    <div className="space-y-6" data-testid="admin-contracts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-barlow font-bold text-[#1A1A1A]">Verträge</h1>
          <p className="text-gray-500 mt-1">Digitale Vertragsverwaltung – Umzüge & Dienstleistungen</p>
        </div>
        <Link to="/admin/vertraege/neu">
          <Button className="bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white" data-testid="contract-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Neuer Vertrag
          </Button>
        </Link>
      </div>

      {/* Tabs (Office-Style) */}
      <div className="inline-flex bg-gray-100 rounded-lg p-1" data-testid="contracts-tabs">
        <Link
          to="/admin/vertraege/neu"
          className="px-4 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-white/60 transition-colors"
          data-testid="contracts-tab-create"
        >
          Neuer Vertrag
        </Link>
        <span
          className="px-4 py-1.5 rounded-md text-sm font-medium bg-white text-[#9B1B30] shadow-sm"
          data-testid="contracts-tab-archive"
        >
          Archiv ({contracts.length})
        </span>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Suche nach Vertragsnr., Name, E-Mail…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="contract-search-input"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {[['all', 'Alle'], ['draft', 'Entwurf'], ['sent', 'Gesendet'], ['signed', 'Unterschrieben'], ['completed', 'Abgeschlossen'], ['cancelled', 'Storniert']].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setFilterStatus(k)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap border transition-colors ${
                    filterStatus === k ? 'bg-[#9B1B30] text-white border-[#9B1B30]' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                  }`}
                  data-testid={`contract-filter-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Lade Verträge…</div>
      ) : contracts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-14 h-14 mx-auto text-gray-300 mb-4" />
            <p className="text-lg font-medium text-gray-700">Noch keine Verträge</p>
            <p className="text-sm text-gray-500 mb-5">Legen Sie Ihren ersten Vertrag an, um loszulegen.</p>
            <Link to="/admin/vertraege/neu">
              <Button className="bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white">
                <Plus className="w-4 h-4 mr-2" /> Vertrag erstellen
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="contracts-list">
          {contracts.map((c) => {
            const status = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
            const total = c.totals?.total_gross ?? 0;
            const customerName = `${c.customer?.first_name || ''} ${c.customer?.last_name || ''}`.trim() || '—';
            return (
              <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`contract-row-${c.contract_number}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-semibold text-[#9B1B30]">{c.contract_number}</span>
                        <Badge variant="outline" className={status.className}>{status.label}</Badge>
                        {c.contract_type && c.contract_type !== 'umzug' && (
                          <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">{c.contract_type}</Badge>
                        )}
                      </div>
                      <p className="font-medium text-[#1A1A1A] truncate">{customerName}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {c.job?.move_date ? `Umzug am ${c.job.move_date}` : 'Kein Datum'} {c.customer?.email ? `· ${c.customer.email}` : ''}
                      </p>
                    </div>
                    <div className="text-left md:text-right">
                      <p className="text-2xl font-bold text-[#1A1A1A]">{Number(total).toFixed(2)} €</p>
                      <p className="text-xs text-gray-500">brutto</p>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      <Link to={`/admin/vertraege/${c.id}`}>
                        <Button size="sm" variant="outline" data-testid={`contract-view-${c.contract_number}`}><Eye className="w-4 h-4" /></Button>
                      </Link>
                      <Link to={`/admin/vertraege/${c.id}/bearbeiten`}>
                        <Button size="sm" variant="outline"><FilePenLine className="w-4 h-4" /></Button>
                      </Link>
                      <Button size="sm" variant="outline" onClick={() => handleDownload(c.id, c.contract_number)} title="PDF" data-testid={`contract-download-${c.contract_number}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleEmail(c.id, c.customer?.email)} title="Per E-Mail senden">
                        <Mail className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDuplicate(c.id)} title="Duplizieren">
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(c.id, c.contract_number)} className="hover:bg-rose-50 hover:text-rose-700" title="Löschen">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
